import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { ChartExportOptions } from '@/components/Dashboard/IndividualChartControls';

export interface ChartData {
  title: string;
  data: any[];
  metadata?: Record<string, any>;
  chartType: string;
  filters?: Record<string, any>;
}

export class ChartExportService {
  /**
   * Export chart as PNG image
   */
  static async exportAsPNG(
    chartElement: HTMLElement,
    options: ChartExportOptions
  ): Promise<void> {
    try {
      const canvas = await html2canvas(chartElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 0,
        removeContainer: true
      });

      const link = document.createElement('a');
      link.download = `${options.customTitle || 'chart'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error exporting chart as PNG:', error);
      throw new Error('Failed to export chart as PNG');
    }
  }

  /**
   * Export chart as PDF
   */
  static async exportAsPDF(
    chartElement: HTMLElement,
    options: ChartExportOptions,
    chartData?: ChartData
  ): Promise<void> {
    try {
      const canvas = await html2canvas(chartElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 0,
        removeContainer: true
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 190; // A4 width minus margins
      const pageHeight = 297; // A4 height
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add title page if metadata is included
      if (options.includeMetadata && chartData) {
        pdf.setFontSize(20);
        pdf.text(chartData.title, 20, 30);
        
        pdf.setFontSize(12);
        pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 50);
        pdf.text(`Chart Type: ${chartData.chartType}`, 20, 60);
        
        if (chartData.filters) {
          pdf.text('Applied Filters:', 20, 80);
          let yPos = 90;
          Object.entries(chartData.filters).forEach(([key, value]) => {
            if (value && value !== 'all') {
              pdf.text(`${key}: ${value}`, 25, yPos);
              yPos += 8;
            }
          });
        }

        if (options.includeData && chartData.data.length > 0) {
          pdf.text(`Data Points: ${chartData.data.length}`, 20, 120);
        }

        pdf.addPage();
      }

      // Add chart image
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if content is longer than one page
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Add data table if requested
      if (options.includeData && chartData?.data && chartData.data.length > 0) {
        pdf.addPage();
        pdf.setFontSize(16);
        pdf.text('Chart Data', 20, 30);
        
        pdf.setFontSize(10);
        let yPos = 50;
        const headers = Object.keys(chartData.data[0]);
        
        // Add headers
        headers.forEach((header, index) => {
          pdf.text(header, 20 + (index * 40), yPos);
        });
        yPos += 10;
        
        // Add data rows
        chartData.data.slice(0, 20).forEach((row, rowIndex) => { // Limit to 20 rows for PDF
          if (yPos > 250) {
            pdf.addPage();
            yPos = 30;
          }
          
          headers.forEach((header, colIndex) => {
            const value = String(row[header] || '');
            pdf.text(value.substring(0, 15), 20 + (colIndex * 40), yPos); // Truncate long values
          });
          yPos += 8;
        });
        
        if (chartData.data.length > 20) {
          pdf.text(`... and ${chartData.data.length - 20} more rows`, 20, yPos + 10);
        }
      }

      pdf.save(`${options.customTitle || 'chart'}.pdf`);
    } catch (error) {
      console.error('Error exporting chart as PDF:', error);
      throw new Error('Failed to export chart as PDF');
    }
  }

  /**
   * Export chart data as CSV
   */
  static exportAsCSV(
    chartData: ChartData,
    options: ChartExportOptions
  ): void {
    try {
      if (!chartData.data || chartData.data.length === 0) {
        throw new Error('No data available for CSV export');
      }

      const headers = Object.keys(chartData.data[0]);
      const csvContent = [
        headers.join(','),
        ...chartData.data.map(row => 
          headers.map(header => {
            const value = row[header];
            // Escape commas and quotes in CSV
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value || '';
          }).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${options.customTitle || 'chart-data'}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting chart as CSV:', error);
      throw new Error('Failed to export chart as CSV');
    }
  }

  /**
   * Main export function that routes to appropriate export method
   */
  static async exportChart(
    chartElement: HTMLElement,
    options: ChartExportOptions,
    chartData?: ChartData
  ): Promise<void> {
    try {
      switch (options.format) {
        case 'png':
          await this.exportAsPNG(chartElement, options);
          break;
        case 'pdf':
          await this.exportAsPDF(chartElement, options, chartData);
          break;
        case 'csv':
          if (chartData) {
            this.exportAsCSV(chartData, options);
          } else {
            throw new Error('Chart data required for CSV export');
          }
          break;
        default:
          throw new Error(`Unsupported export format: ${options.format}`);
      }
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  }

  /**
   * Get chart element by chart ID
   */
  static getChartElement(chartId: string): HTMLElement | null {
    return document.getElementById(chartId) || 
           document.querySelector(`[data-chart-id="${chartId}"]`) ||
           document.querySelector(`[data-testid="chart-${chartId}"]`);
  }

  /**
   * Prepare chart data for export
   */
  static prepareChartData(
    title: string,
    data: any[],
    chartType: string,
    filters?: Record<string, any>,
    metadata?: Record<string, any>
  ): ChartData {
    return {
      title,
      data,
      chartType,
      filters,
      metadata
    };
  }
}
